import { Modal } from '@components/Modal';
import { Button, Loader, TrashIcon, Typography } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { FC, MouseEvent } from 'react';
import { useState } from 'react';

interface ConfirmModalProps {
	onRemove: (id: string) => Promise<void>;
	titleKey: string;
	submitKey: string;
	cancelKey: string;
	id: string;
	disabled?: boolean;
}

const cancelWrapper = (fn: () => void) => (e: MouseEvent<HTMLElement>) => {
	e.preventDefault();
	e.stopPropagation();
	fn();
};

export const ConfirmModal: FC<ConfirmModalProps> = ({
	onRemove,
	id,
	titleKey,
	submitKey,
	cancelKey,
	disabled = false,
}) => {
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const closeModal = () => setIsOpened(false);
	const openModal = () => setIsOpened(true);

	const submitHandler = async () => {
		setIsLoading(true);

		try {
			await onRemove(id);
		} finally {
			closeModal();
			setIsLoading(false);
		}
	};

	return (
		<>
			<button
				className={clsx(
					'w-space-32 h-space-32 rounded-full bg-neutral-50 transition-colors duration-200',
					disabled
						? 'cursor-not-allowed hover:none'
						: 'cursor-pointer  hover:bg-status-error-50 hover:text-status-error-500',
				)}
				disabled={disabled}
				onClick={cancelWrapper(openModal)}
				type="button"
			>
				<TrashIcon className="w-space-16 h-space-16 m-auto" />
			</button>
			<Modal className="space-y-space-24 text-center" isOpened={isOpened}>
				<div className="inline-block p-space-16 rounded-full border border-status-error-300 bg-status-error-50">
					<TrashIcon className="text-status-error-500" />
				</div>
				<Typography className="overflow-hidden text-ellipsis" level="h2">
					{titleKey}
				</Typography>
				<div className="space-y-space-8">
					<Button
						className="w-full"
						disabled={isLoading}
						onClick={cancelWrapper(submitHandler)}
						variant="primary-black"
					>
						{isLoading && <Loader className="!w-space-24 !h-space-24" />}
						{submitKey}
					</Button>
					<Button
						className="w-full"
						disabled={isLoading}
						onClick={cancelWrapper(closeModal)}
						variant="secondary"
					>
						{cancelKey}
					</Button>
				</div>
			</Modal>
		</>
	);
};
